/**
 * Shared chart chrome for the Stats page — one source for colour, ink and the
 * Highcharts tooltip/axis/legend styling, so thirteen panels read as one surface.
 *
 * The categorical palette leads with the BankingBridge brand purple and was
 * validated on a white card surface (OKLCH lightness band, chroma floor,
 * protan/deutan separation on adjacent pairs, WCAG contrast). Assign slots in
 * this order and never cycle them — a 9th series folds into "Other".
 *
 * Aqua, yellow and magenta carry a contrast warning on white, so any mark using
 * them always ships a direct label or a table alongside it.
 */
export const STATS_PALETTE = [
  "#786ff6", // 1 brand purple
  "#eb6834", // 2 orange
  "#1baf7a", // 3 aqua
  "#eda100", // 4 yellow
  "#e87ba4", // 5 magenta
  "#008300", // 6 green
  "#2a78d6", // 7 blue
  "#e34948" // 8 red
] as const

/** Only the first three slots separate on every pairing — pies, maps and meters cap here. */
export const STATS_PALETTE_ALL_PAIRS = STATS_PALETTE.slice(0, 3)

/**
 * Ordinal purple ramp for discrete ordered marks (funnel stages, progress
 * columns). The light end still clears 2:1 on white.
 */
export const ORDINAL_PURPLE = [
  "#ada6fa",
  "#918af7",
  "#786ff6",
  "#5b4fdd",
  "#4436b0",
  "#2e2382"
] as const

/** Sequential ends for continuous magnitude (the choropleth colour axis). */
export const SEQUENTIAL_PURPLE = {
  min: "#F1EFFE",
  max: "#4436b0"
} as const

/** Recessive chrome — grid and axis ink must never compete with the marks. */
export const CHART_INK = {
  grid: "#EEF0F5",
  axis: "#D8DCE6",
  label: "#6B7280",
  title: "#9AA0AC",
  crosshair: "rgba(120, 111, 246, 0.22)",
  tooltipBg: "#2A2438",
  surface: "#FFFFFF"
} as const

export const CHART_FONT =
  'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'

/** API-sourced labels (UTM values, URLs, brand names) are untrusted — never put them into tooltip HTML raw. */
export const escapeHtml = (value: unknown): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")

/** Tooltip chrome shared by every Stats chart: dark surface, no shadow. */
export const statsTooltip = (
  extra: Highcharts.TooltipOptions = {}
): Highcharts.TooltipOptions => ({
  backgroundColor: CHART_INK.tooltipBg,
  borderColor: CHART_INK.tooltipBg,
  borderRadius: 6,
  borderWidth: 0,
  shadow: false,
  padding: 10,
  style: {
    color: "#ffffff",
    fontSize: "12px",
    fontFamily: CHART_FONT
  },
  ...extra
})

/** Inline styles for tooltips that render their own HTML bubble. */
export const TOOLTIP_BUBBLE_STYLE = `background:${
  CHART_INK.tooltipBg
};color:#fff;border-radius:6px;padding:10px;font-size:12px;line-height:1.4;font-family:${CHART_FONT.replace(
  /"/g,
  "'"
)};`

/** Value axis: solid hairline grid, muted labels, no axis line. */
export const valueAxis = (
  title = "",
  extra: Highcharts.YAxisOptions = {}
): Highcharts.YAxisOptions => ({
  title: {
    text: title,
    style: { color: CHART_INK.title, fontSize: "11px" }
  },
  // Solid hairline one shade off the surface — dashing reads as "threshold".
  gridLineColor: CHART_INK.grid,
  gridLineWidth: 1,
  lineWidth: 0,
  tickLength: 0,
  labels: {
    style: {
      color: CHART_INK.label,
      fontSize: "11px",
      fontFamily: CHART_FONT
    }
  },
  ...extra
})

/** Placeholder value axis drawn behind the "No data yet" message. */
export const emptyValueAxis = (title = ""): Highcharts.YAxisOptions =>
  valueAxis(title, {
    gridLineWidth: 0,
    lineWidth: 1,
    lineColor: CHART_INK.axis,
    tickInterval: 5,
    min: 5,
    max: 25
  })

export const categoryAxis = (
  extra: Highcharts.XAxisOptions = {}
): Highcharts.XAxisOptions => ({
  type: "category",
  lineColor: CHART_INK.axis,
  tickColor: CHART_INK.axis,
  tickLength: 4,
  labels: {
    style: {
      color: CHART_INK.label,
      fontSize: "11px",
      fontFamily: CHART_FONT
    }
  },
  ...extra
})

/** Legend chrome. A legend is present for two or more series and never for one. */
export const statsLegend = (
  extra: Highcharts.LegendOptions = {}
): Highcharts.LegendOptions => ({
  enabled: true,
  align: "left",
  verticalAlign: "bottom",
  layout: "horizontal",
  symbolRadius: 2,
  symbolHeight: 10,
  symbolWidth: 10,
  itemDistance: 18,
  itemStyle: {
    color: "#4B5563",
    fontWeight: "400",
    fontSize: "12px",
    fontFamily: CHART_FONT
  },
  itemHoverStyle: { color: "#111827" },
  itemHiddenStyle: { color: "#C4C7CE" },
  ...extra
})
